import { apiFetch } from './http';
import { toAppError } from '../utils/appErrors';

export type SimpleInOutStatusValue = 'in' | 'remote' | 'busy' | 'break' | 'out' | 'sick' | 'vacation';

export interface SimpleInOutStatus {
  status: SimpleInOutStatusValue | string;
  comment?: string | null;
  automatic?: boolean | null;
  user_id?: string | null;
  changed_by_user_id?: string | null;
  created_at?: number | null;
}

export interface SimpleInOutGroup {
  id: string;
  name: string;
}

export interface SimpleInOutMembership {
  id?: string | null;
  group_id?: string | null;
  group?: SimpleInOutGroup | null;
}

export type SimpleInOutPhoneKind = 'mobile' | 'office' | 'home' | 'phone';

/** A phone number normalized by the server's dotboard route. */
export interface SimpleInOutPhoneNumber {
  number: string;
  kind: SimpleInOutPhoneKind;
}

/** Portal profile details the dotboard route joins in by email. */
export interface SimpleInOutPortalContact {
  profile_id: string;
  job_title: string | null;
  headshot_url: string | null;
}

export interface SimpleInOutUser {
  id: string;
  name?: string | null;
  email?: string | null;
  details?: string | null;
  image_url?: string | null;
  cached_at?: string | null;
  status?: SimpleInOutStatus | null;
  latest?: SimpleInOutStatus | null;
  memberships?: SimpleInOutMembership[];
  groups?: SimpleInOutGroup[];
  role?: { id: string; name: string } | null;
  phone_numbers?: SimpleInOutPhoneNumber[];
  portal?: SimpleInOutPortalContact | null;
}

export interface SimpleInOutConfigPayload {
  ok: boolean;
  configured: {
    client_id: boolean;
    client_secret: boolean;
    redirect_uri: boolean;
    access_token: boolean;
    refresh_token: boolean;
  };
  redirect_uri: string | null;
  scopes: string[];
  integration_ready?: boolean;
  verification?: {
    ready: boolean;
    configured: boolean;
    token: boolean;
    verified: boolean;
    verified_at: string | null;
    checked_at: string | null;
    token_source: string;
    last_error?: {
      status?: number | null;
      message?: string | null;
    } | null;
  };
  values?: {
    client_id?: string;
    redirect_uri?: string;
    scopes?: string;
  };
  sources?: Record<string, string>;
  encrypted_at_rest?: boolean;
  base_url: string;
  api_base_url: string;
  missing_for_authorize: string[];
  missing_for_token_exchange: string[];
  token_source: 'env' | 'stored' | 'none' | string;
  stored_token: boolean;
}

export interface SimpleInOutConfigInput {
  clientId: string;
  clientSecret?: string;
  scopes: string;
  redirectUri: string;
}

export interface SimpleInOutTokenSummary {
  token_type: string | null;
  scope: string | null;
  expires_in: number | null;
  created_at: number | null;
  expires_at: number | null;
  user: unknown;
  has_access_token: boolean;
  has_refresh_token: boolean;
}

export interface SimpleInOutMeStatusPayload {
  ok: boolean;
  mapped: boolean;
  email: string;
  token_source: 'env' | 'stored' | 'none' | string;
  user: SimpleInOutUser | null;
  status: SimpleInOutStatus | null;
  stale?: boolean;
  unavailable?: boolean;
  cached_at?: string | null;
  cache_source?: string | null;
  last_error?: {
    status?: number | null;
    message?: string | null;
  } | null;
}

export interface SimpleInOutAvailabilityPayload {
  ok: boolean;
  enabled: boolean;
  verified: boolean;
  verified_at: string | null;
  checked_at: string | null;
  token_source: 'env' | 'stored' | 'none' | string;
  last_error?: {
    status?: number | null;
    message?: string | null;
  } | null;
}

export interface SimpleInOutUsersPayload {
  ok: boolean;
  token_source: 'env' | 'stored' | 'none' | string;
  data: {
    users?: SimpleInOutUser[];
    meta?: {
      paging?: {
        page?: number;
        page_size?: number;
        page_count?: number;
        count?: number;
        next_page?: number | null;
      };
      trusted_cached_at?: string;
      last_updated_at?: Record<string, number>;
    };
  };
  rateLimit?: { limit: number | null; remaining: number | null } | null;
  refreshed?: boolean;
}

export interface SimpleInOutBoardPayload extends SimpleInOutUsersPayload {
  enabled?: boolean;
  portal_unavailable?: boolean;
  stale?: boolean;
  unavailable?: boolean;
  cached_at?: string | null;
  cache_source?: string | null;
  last_error?: {
    status?: number | null;
    message?: string | null;
  } | null;
}

export interface SimpleInOutUserStatusesPayload {
  ok: boolean;
  token_source: 'env' | 'stored' | 'none' | string;
  users: Array<{
    email: string;
    mapped: boolean;
    name?: string | null;
    status: SimpleInOutStatus | null;
    stale?: boolean;
    unavailable?: boolean;
    cached_at?: string | null;
    cache_source?: string | null;
    last_error?: {
      status?: number | null;
      message?: string | null;
    } | null;
  }>;
  unavailable?: boolean;
  refreshed?: boolean;
}

export interface SimpleInOutAuthorizeUrlPayload {
  ok: boolean;
  authorizeUrl: string;
  redirect_uri: string;
}

export interface SimpleInOutExchangePayload {
  ok: boolean;
  persisted: boolean;
  token: SimpleInOutTokenSummary;
}

export interface SimpleInOutDiagnosticEntry {
  at: string;
  route?: string | null;
  purpose?: string | null;
  user_email?: string | null;
  requested_email?: string | null;
  email_count?: number | null;
  token_source?: string | null;
  method?: string | null;
  path?: string | null;
  query?: Record<string, string | string[]>;
  status?: number | null;
  ok?: boolean;
  guarded?: boolean;
  rate_limit?: { limit: number | null; remaining: number | null } | null;
  duration_ms?: number | null;
  error?: {
    status?: number | null;
    message?: string | null;
  } | null;
}

export interface SimpleInOutDiagnosticsPayload {
  ok: boolean;
  diagnostics: {
    updated_at: string | null;
    rate_limit: {
      status?: number | null;
      last_429_at?: string | null;
      guarded_until?: string | null;
      guard_seconds?: number | null;
      rate_limit?: { limit: number | null; remaining: number | null } | null;
      last_error?: {
        status?: number | null;
        message?: string | null;
      } | null;
    } | null;
    totals_24h: {
      logged: number;
      upstream: number;
      guarded: number;
      rate_limited: number;
    };
    by_purpose_24h: Array<{
      key: string;
      total: number;
      upstream: number;
      guarded: number;
      rate_limited: number;
    }>;
    recent: SimpleInOutDiagnosticEntry[];
  };
}

async function parseJson<T>(res: Response): Promise<T> {
  const text = await res.text();
  let payload: Record<string, unknown> = {};
  try {
    payload = text ? JSON.parse(text) as Record<string, unknown> : {};
  } catch (error) {
    throw toAppError(error, 'The Simple In/Out service returned an invalid response.', 'simple_inout.response');
  }
  if (!res.ok) {
    const code = typeof payload.code === 'string' ? payload.code : undefined;
    throw toAppError(
      { status: res.status, code, payload },
      'The Simple In/Out request could not be completed.',
      'simple_inout.request',
    );
  }
  return payload as T;
}

export async function fetchSimpleInOutConfig(): Promise<SimpleInOutConfigPayload> {
  const res = await apiFetch('/simple-inout/config');
  return parseJson<SimpleInOutConfigPayload>(res);
}

export async function fetchSimpleInOutDiagnostics(): Promise<SimpleInOutDiagnosticsPayload> {
  const res = await apiFetch('/simple-inout/diagnostics');
  return parseJson<SimpleInOutDiagnosticsPayload>(res);
}

export async function saveSimpleInOutConfig(input: SimpleInOutConfigInput): Promise<SimpleInOutConfigPayload> {
  const res = await apiFetch('/simple-inout/config', {
    method: 'POST',
    body: JSON.stringify({
      client_id: input.clientId,
      client_secret: input.clientSecret,
      scopes: input.scopes,
      redirect_uri: input.redirectUri,
    }),
  });
  return parseJson<SimpleInOutConfigPayload>(res);
}

export async function clearSimpleInOutConfig(): Promise<SimpleInOutConfigPayload> {
  const res = await apiFetch('/simple-inout/config', { method: 'DELETE' });
  return parseJson<SimpleInOutConfigPayload>(res);
}

export async function fetchSimpleInOutAuthorizeUrl(scope = 'read'): Promise<SimpleInOutAuthorizeUrlPayload> {
  const res = await apiFetch(`/simple-inout/authorize-url?scope=${encodeURIComponent(scope)}`);
  return parseJson<SimpleInOutAuthorizeUrlPayload>(res);
}

export async function exchangeSimpleInOutCode(code: string): Promise<SimpleInOutExchangePayload> {
  const res = await apiFetch('/simple-inout/oauth/exchange', {
    method: 'POST',
    body: JSON.stringify({ code }),
  });
  return parseJson<SimpleInOutExchangePayload>(res);
}

export async function refreshSimpleInOutToken(): Promise<SimpleInOutExchangePayload> {
  const res = await apiFetch('/simple-inout/oauth/refresh', { method: 'POST' });
  return parseJson<SimpleInOutExchangePayload>(res);
}

export async function fetchSimpleInOutTokenInfo(): Promise<{ ok: boolean; token: unknown; rateLimit?: { limit: number | null; remaining: number | null } | null }> {
  const res = await apiFetch('/simple-inout/token/info');
  return parseJson(res);
}

export async function fetchSimpleInOutUsers(pageSize = 100): Promise<SimpleInOutUsersPayload> {
  const params = new URLSearchParams({
    page_size: String(pageSize),
    expand: 'status,memberships.group,role',
    sort: 'name',
  });
  const res = await apiFetch(`/simple-inout/users?${params.toString()}`);
  return parseJson<SimpleInOutUsersPayload>(res);
}

export async function fetchMySimpleInOutBoard(options: { pageSize?: number; groupId?: string } = {}): Promise<SimpleInOutBoardPayload> {
  const params = new URLSearchParams({
    page_size: String(options.pageSize ?? 150),
  });
  if (options.groupId && options.groupId !== 'all') {
    params.set('group_id', options.groupId);
  }
  const res = await apiFetch(`/simple-inout/me/board?${params.toString()}`);
  return parseJson<SimpleInOutBoardPayload>(res);
}

export async function fetchSimpleInOutStatusesByEmail(emails: string[]): Promise<SimpleInOutUserStatusesPayload> {
  const uniqueEmails = Array.from(new Set(
    emails
      .map((email) => String(email || '').trim().toLowerCase())
      .filter(Boolean),
  )).slice(0, 80);
  if (uniqueEmails.length === 0) {
    return { ok: true, token_source: 'none', users: [] };
  }
  const params = new URLSearchParams({ emails: uniqueEmails.join(',') });
  const res = await apiFetch(`/simple-inout/me/statuses?${params.toString()}`);
  return parseJson<SimpleInOutUserStatusesPayload>(res);
}

export async function fetchMySimpleInOutStatus(): Promise<SimpleInOutMeStatusPayload> {
  const res = await apiFetch('/simple-inout/me/status');
  return parseJson<SimpleInOutMeStatusPayload>(res);
}

export async function fetchMySimpleInOutAvailability(): Promise<SimpleInOutAvailabilityPayload> {
  const res = await apiFetch('/simple-inout/me/availability');
  return parseJson<SimpleInOutAvailabilityPayload>(res);
}

export async function updateMySimpleInOutStatus(status: SimpleInOutStatusValue, comment?: string): Promise<SimpleInOutMeStatusPayload> {
  const res = await apiFetch('/simple-inout/me/status', {
    method: 'POST',
    body: JSON.stringify({ status, comment }),
  });
  return parseJson<SimpleInOutMeStatusPayload>(res);
}

export type SimpleInOutJobStatusSync = {
  ok: boolean;
  checked?: number;
  applied?: number;
  cleared?: number;
  skipped?: string;
};

/** Notes (or clears) where you are on your Simple In/Out status after your own job clock action. */
export async function syncMySimpleInOutJobStatus(): Promise<SimpleInOutJobStatusSync> {
  const res = await apiFetch('/simple-inout/me/job-status', { method: 'POST' });
  return parseJson<SimpleInOutJobStatusSync>(res);
}
